import { forwardRef, type InputHTMLAttributes } from 'react';
import { Search } from 'lucide-react';
import { clsx } from '../clsx';
import { useFieldIds } from '../hooks';
import { FieldFrame } from './FieldFrame';
import { Icon } from './Icon';
import { KeyboardHint } from './KeyboardHint';
import styles from './SearchField.module.css';

export interface SearchFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /** Accessible name (rendered visually hidden unless `labelVisible`). */
  label: string;
  labelVisible?: boolean | undefined;
  /** Leading search icon (16 px). */
  icon?: boolean | undefined;
  /** Shortcut badge at the end, e.g. "⌘K". */
  shortcut?: string | undefined;
  /** `card` = the list filter ("Name, Telefon, E-Mail"); `sunken` = the topbar trigger look. */
  tone?: 'card' | 'sunken' | undefined;
  size?: 'default' | 'compact' | undefined;
}

/** Search input with icon and optional keyboard shortcut badge. */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  {
    label,
    labelVisible = false,
    icon = true,
    shortcut,
    tone = 'card',
    size,
    className,
    id,
    disabled,
    ...rest
  },
  ref,
) {
  const ids = useFieldIds(id, {});
  return (
    <FieldFrame
      htmlFor={ids.inputId}
      label={label}
      labelHidden={!labelVisible}
      hintId={ids.hintId}
      errorId={ids.errorId}
      disabled={disabled}
      leading={icon ? <Icon icon={Search} size={16} /> : undefined}
      trailing={shortcut ? <KeyboardHint keys={shortcut} /> : undefined}
      tone={tone}
      size={size}
      controlClassName={size === 'compact' ? undefined : styles.control}
      className={className}
    >
      <input
        ref={ref}
        id={ids.inputId}
        type="search"
        className={clsx(styles.input)}
        disabled={disabled}
        {...rest}
      />
    </FieldFrame>
  );
});
